import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { connect } from 'formik';
import { Grid } from '@material-ui/core';

import { useStore } from 'core/store';
import { Card } from 'core/components/card';
import { getPolicyType } from 'core/helpers/policy-type';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import Section from 'core/components/section';
import { FormField } from 'core/components/form';
import { awsDateTimeFormatter } from 'core/helpers/formatters';
import { getCoverage } from 'offer/helpers/get-coverage';
import { brandConstants } from 'brand-config';
import { isStressFreeSwitchHidden } from '../../helper';
import useStyles from './cancel-current-policy.styles';

const CancelCurrentPolicy = observer(function CancelCurrentPolicy({ formik }) {
  const { offer: offerStore } = useStore();
  const { offer } = offerStore;

  const classes = useStyles();
  // #36286: no Stress-Free Switch for a brand that opted out, for underwriters and agents alike
  // (the checkout's initial values seed null for the same brand — see helper.js).
  const hideStressFreeSwitch = isStressFreeSwitchHidden();
  const selectedOption = getQuoteFriendlySelectedOption(offerStore.selectedOption || 'HA');

  const { hasHome, hasAuto, isRenters } = getPolicyType(
    selectedOption,
    offer?.quote?.noBindHome,
    offer?.quote?.noBindAuto
  );

  const { hasUmbrellaCoverage, hasJewelryCoverage } = useMemo(() => getCoverage(offer), [offer]);

  const {
    setFieldValue,
    values: { breakupWithAuto, breakupWithHomeowners, breakupWithRenters, breakupESignature }
  } = formik;
  const onChangeBreakupSignature = useCallback(
    (_, checked) => {
      setFieldValue('breakupESignature', checked ? awsDateTimeFormatter(new Date()) : null);
    },
    [setFieldValue]
  );

  // The checkout seeds an unsigned `breakupESignature` as `false` (#37497). Leave it alone on mount, so
  // an untouched checkout isn't treated as edited (which blocked "Copy purchase link"):
  // - every switch off with a signature left behind → clear it;
  // - the rep turns a switch on while unsigned → `null`.
  // Either way the schema's e-signature rule blocks the form until the box is ticked.
  const anySwitchOn = Boolean(breakupWithAuto || breakupWithHomeowners || breakupWithRenters);
  const anySwitchWasOn = useRef(anySwitchOn);
  useEffect(() => {
    const switchTurnedOn = anySwitchOn && !anySwitchWasOn.current;
    anySwitchWasOn.current = anySwitchOn;
    if ((!anySwitchOn && breakupESignature) || (switchTurnedOn && breakupESignature === false)) {
      setFieldValue('breakupESignature', null);
    }
  }, [setFieldValue, anySwitchOn, breakupESignature]);

  const homeLabel = useMemo(() => {
    const umbrellaLabel = hasUmbrellaCoverage ? '/Umbrella' : '';
    const jewelryLabel = hasJewelryCoverage ? '/Jewelry' : '';
    return `Cancel Prior Home${umbrellaLabel}${jewelryLabel} Insurance As Part Of Purchase`;
  }, [hasUmbrellaCoverage, hasJewelryCoverage]);

  const autoLabel = useMemo(() => {
    const rentersLabel = isRenters ? '/Renters' : '';
    return `Cancel Prior Auto${rentersLabel} Insurance As Part Of Purchase`;
  }, [isRenters]);

  if (hideStressFreeSwitch) {
    return null;
  }

  return (
    <Section title="Stress - Free Switch" showSeparator={false} type="SubSection">
      <Card className={classes.cardContainer} type="primaryEditable">
        <Grid container item xs={12} spacing={4}>
          {hasHome && (
            <Grid item xs={12}>
              <FormField name="breakupWithHomeowners" label={homeLabel} type="switch" mode="light" />
              {breakupWithHomeowners && (
                <Grid container item xs={12} spacing={4} className={classes.priorInsuranceFields}>
                  <FormField
                    name="currentHomeownersCarrier"
                    label="Prior Home Insurance Provider"
                    type="string"
                    mode="light"
                    xs={6}
                  />
                  <FormField
                    className={classes.formWithPadding}
                    name="currentHomeownersCarrierPolicyNumber"
                    label="Policy Number From Prior Insurance"
                    type="string"
                    mode="light"
                    xs={6}
                  />
                </Grid>
              )}
            </Grid>
          )}
          {hasAuto && (
            <Grid item xs={12} className={breakupWithHomeowners && classes.cancelPriorInsuranceLine}>
              <FormField name="breakupWithAuto" label={autoLabel} type="switch" mode="light" />
              {breakupWithAuto && (
                <Grid container item xs={12} spacing={4} className={classes.priorInsuranceFields}>
                  <FormField
                    xs={6}
                    name="currentAutoCarrier"
                    label="Prior Auto Insurance Provider"
                    type="string"
                    mode="light"
                  />
                  <FormField
                    xs={6}
                    className={classes.formWithPadding}
                    name="currentAutoCarrierPolicyNumber"
                    label="Policy Number From Prior Insurance"
                    type="string"
                    mode="light"
                  />
                </Grid>
              )}
            </Grid>
          )}
          {isRenters && (
            <Grid item xs={12} className={breakupWithAuto && classes.cancelPriorInsuranceLine}>
              <FormField
                name="breakupWithRenters"
                label={'Cancel Prior Renters Insurance As Part Of Purchase'}
                type="switch"
                mode="light"
              />
              {breakupWithRenters && (
                <Grid container item xs={12} spacing={4} className={classes.priorInsuranceFields}>
                  <FormField
                    xs={6}
                    name="currentRentersCarrier"
                    label="Prior Renters Insurance Provider"
                    type="string"
                    mode="light"
                  />
                  <FormField
                    xs={6}
                    className={classes.formWithPadding}
                    name="currentRentersCarrierPolicyNumber"
                    label="Policy Number From Prior Insurance"
                    type="string"
                    mode="light"
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
        {anySwitchOn && (
          <Grid item xs={12} className={classes.breakUpESignature}>
            <FormField
              name="breakupESignature"
              type="checkbox"
              onChange={onChangeBreakupSignature}
              label={`You authorize ${brandConstants.SHORT_NAME} to contact the above insurer(s) on your behalf to request cancellation of your current policy or policies, and acknowledge that this process will require that ${brandConstants.SHORT_NAME} obtain and use your electronic signature. You acknowledge that your electronic signature shall have the same force and effect of a handwritten signature for purposes of validity, enforcement, and admissibility. You also acknowledge that ${brandConstants.SHORT_NAME} cannot guarantee that the other insurer(s) will process the cancellation request and that you should confirm cancellation directly with the insurer(s) as needed.`}
              mode="light"
              fast={false}
              topAlignCheckbox
            />
          </Grid>
        )}
      </Card>
    </Section>
  );
});

CancelCurrentPolicy.propTypes = {
  formik: PropTypes.object.isRequired
};

export default connect(CancelCurrentPolicy);
